/**
 * @module interaction
 *
 * Ephemeral Interaction State — Jotai atoms
 *
 * Phase 1 of the State Management Migration (STATE_MANAGEMENT_MIGRATION_PLAN.md).
 * All atoms in this module replace Redux fields that were updated at pointer/hover
 * frequency and had no business in a serialisable, cross-subscriber store.
 *
 * Ownership contract (STATE_OWNERSHIP_CONTRACT.md §3.3 — Jotai layer):
 *  - Updated at pointer or animation frequency
 *  - Meaningful only for immediate visual feedback in a narrow set of subscribers
 *  - NOT persisted to session/local storage
 *  - NOT synchronised across browser tabs
 *  - Reset implicitly when the component tree unmounts (Jotai Provider scope)
 *
 * Cross-tab rule (§1.6 of migration plan):
 *  No atom in this file may be covered by redux-state-sync or any BroadcastChannel
 *  bridge. Any future cross-tab hover requirement must be justified by a named product
 *  requirement and recorded in the ownership contract Decision Log.
 */
import { useCallback, useEffect, useRef } from "react";

import { atom, useSetAtom, useStore } from "jotai";

// ---------------------------------------------------------------------------
// Hover Legend State
// ---------------------------------------------------------------------------

/**
 * All four hover-legend fields grouped into a single struct so that a single
 * atomic write keeps them consistent and avoids intermediate render ticks
 * where item and group are out of sync.
 *
 * @metadata
 *  Layer        : Jotai
 *  Owner module : src/atoms/interaction.ts
 *  Scope        : global (all charts share one hover state at a time)
 *  Persist      : no
 *  Cross-tab    : no
 *  Reset on project switch : yes (implicit — component unmount)
 *  Reset on auth change    : yes (implicit — component unmount)
 *
 * Migrated from:
 *  groupBy.hoverLegendItem          (Redux groupBySlice — @deprecated)
 *  groupBy.hoverLegendGroup         (Redux groupBySlice — @deprecated)
 *  groupBy.hoverLegendId            (Redux groupBySlice — @deprecated)
 *  groupBy.hoverLegendItemUnfiltered(Redux groupBySlice — @deprecated)
 */
// Fields use empty string rather than null as the "no hover" sentinel.
// This matches the pre-migration Redux initial state, eliminates null checks
// at every call site, and keeps string operations (comparisons, includes, etc.)
// free of null guards. EMPTY_HOVER_LEGEND is the canonical empty value.
export interface HoverLegendState {
  readonly item: string;
  readonly group: string;
  readonly id: string;
  readonly itemUnfiltered: string;
  readonly isMultiProductChart: boolean;
}

/**
 * Canonical empty value for hover legend state.
 * Used by all write sites on mouse-leave to reset hover state.
 */
export const EMPTY_HOVER_LEGEND: HoverLegendState = {
  item: "",
  group: "",
  id: "",
  itemUnfiltered: "",
  isMultiProductChart: false
};

/**
 * Trailing delay before the charts and the map apply a hover or selection
 * highlight. Shared so both surfaces change in the same beat.
 */
export const SERIES_HIGHLIGHT_DELAY_MS = 200;

/**
 * Base writable atom — the single write target for hover legend state.
 *
 * Only write sites (mouse enter/leave handlers) should call `set` on this atom.
 * All read sites must subscribe to the narrow derived atoms below so that a
 * component reading only `item` does not re-render when only `group` changes.
 */
export const hoverLegendBaseAtom = atom<HoverLegendState>(EMPTY_HOVER_LEGEND);

/**
 * Read-only derived slice atoms.
 * Each atom subscribes only to its own field on the base atom; a change to a
 * sibling field does NOT trigger a re-render for subscribers of these atoms.
 */
export const hoverLegendItemAtom = atom<string>((get) => get(hoverLegendBaseAtom).item);
export const hoverLegendGroupAtom = atom<string>((get) => get(hoverLegendBaseAtom).group);
export const hoverLegendIdAtom = atom<string>((get) => get(hoverLegendBaseAtom).id);
export const hoverLegendItemUnfilteredAtom = atom<string>(
  (get) => get(hoverLegendBaseAtom).itemUnfiltered
);
export const hoverLegendIsMultiProductChartAtom = atom<boolean>(
  (get) => get(hoverLegendBaseAtom).isMultiProductChart
);

/**
 * Write-only atom with structural equality check.
 *
 * Prevents unnecessary subscriber notifications when the same hover state is
 * written repeatedly — e.g., when bubbling pointer events fire within a single
 * legend item's child elements.
 */
export const setHoverLegendAtom = atom(null, (get, set, next: HoverLegendState) => {
  const current = get(hoverLegendBaseAtom);
  if (
    current.item === next.item &&
    current.group === next.group &&
    current.id === next.id &&
    current.itemUnfiltered === next.itemUnfiltered &&
    current.isMultiProductChart === next.isMultiProductChart
  ) {
    return;
  }
  set(hoverLegendBaseAtom, next);
});

// Module-level microtask state for hover legend writes.
// Shared across all component instances so that rapid leave→enter transitions
// within the same event-loop tick collapse into a single atom update.
let _hoverWriteScheduled = false;
let _hoverWritePending: HoverLegendState = EMPTY_HOVER_LEGEND;

// Tracks which source last claimed hover ownership.
// "map"    — last write was initiated by a map pointer event (useMapHoverLegend)
// "chart"  — last write was initiated by a chart ECharts/ZRender event (useChartMouseInteractions, useChartZRender)
// "legend" — last write was initiated by useHoverLegendWriter (chart / UI legend item)
// "none"   — hover state is empty
//
// The map's debounced mouseleave (50 ms) must not clear state that a legend
// item claimed on pointerenter — even when the values happen to be identical
// and the legend item's microtask write is a no-op due to equality check.
type HoverOwner = "map" | "chart" | "legend" | "none";
let _hoverOwner: HoverOwner = "none";

/** Called by map hover code to take or release ownership of the hover state. */
export function claimHoverOwnership(owner: HoverOwner): void {
  _hoverOwner = owner;
}

/** Returns which source currently owns the hover state. */
export function getHoverOwner(): HoverOwner {
  return _hoverOwner;
}

/**
 * Returns a microtask-debounced write function for hover legend state.
 *
 * All instances share a single module-level pending value. When the pointer
 * moves from item A → B, A's pointerleave schedules an EMPTY write, then B's
 * pointerenter (same tick) replaces the pending value with B's hover state.
 * The microtask fires after the current event handlers complete, writing only
 * the final winning value via the equality-checked `setHoverLegendAtom`.
 *
 * Using queueMicrotask instead of setTimeout(50) eliminates the 50 ms latency
 * that caused fast legend hovers to feel unresponsive, while still collapsing
 * synchronous leave→enter pairs within a single pointer event cycle.
 */
export function useHoverLegendWriter(): (next: HoverLegendState) => void {
  const set = useSetAtom(setHoverLegendAtom);
  const setRef = useRef(set);
  setRef.current = set;

  return useCallback((next: HoverLegendState) => {
    _hoverWritePending = next;
    if (next.item) {
      // Synchronously claim ownership on enter so that any concurrent map/chart
      // mouseleave debounce sees "legend" as the owner and skips its clear.
      _hoverOwner = "legend";
    }
    // Do NOT change ownership synchronously for empty (leave) writes.
    // If the pointer briefly crosses a legend item while in transit from the map,
    // the item's pointerleave fires an empty write. Setting owner to "none" here
    // would prevent the map's own 50 ms debounce from clearing, leaving the
    // highlight stuck. Ownership release is deferred to the microtask where we
    // confirm legend still owns the state.
    if (!_hoverWriteScheduled) {
      _hoverWriteScheduled = true;
      queueMicrotask(() => {
        _hoverWriteScheduled = false;
        const pending = _hoverWritePending;
        // Only write an empty state if legend currently owns the hover.
        // If map or chart owns it, their own debounced leave handlers are
        // responsible for clearing — we must not interfere.
        if (!pending.item && _hoverOwner !== "legend") {
          return;
        }
        if (!pending.item) {
          _hoverOwner = "none";
        }
        setRef.current(pending);
      });
    }
  }, []);
}

/**
 * Subscribes to hover legend atoms at the Jotai store level and toggles the
 * `highlighted` CSS class on the given DOM element. This avoids triggering a
 * React re-render for every legend item on every hover change — the class
 * toggle is a direct DOM mutation via `classList.toggle`.
 *
 * @param ref - React ref to the wrapper DOM element
 * @param title - This legend item's title (compared against hoverLegendItem)
 * @param id - This legend item's id (compared against hoverLegendId, optional match)
 * @param transformTitle - Optional function to transform the hover title before comparison
 */
export function useHoverHighlightClass(
  ref: React.RefObject<HTMLElement>,
  title: string,
  id: Maybe<string>,
  transformTitle?: (t: string) => string
): void {
  const store = useStore();

  useEffect(() => {
    const update = () => {
      const hoverItem = store.get(hoverLegendItemAtom);
      const hoverId = store.get(hoverLegendIdAtom);
      const hoverTitle = transformTitle ? transformTitle(hoverItem) : hoverItem;
      const titlesMatch = hoverTitle === title;
      const idsMatch = hoverId ? hoverId === id : true;
      ref.current?.classList.toggle("highlighted", titlesMatch && idsMatch);
    };

    // Run once immediately to set the initial state
    update();

    // Subscribe to the base atom — fires whenever any hover field changes
    const unsub = store.sub(hoverLegendBaseAtom, update);
    return unsub;
  }, [store, title, id, transformTitle, ref]);
}

// ---------------------------------------------------------------------------
// Hover Data Point
// ---------------------------------------------------------------------------

/**
 * Scatter / cross-plot chart hovered data point.
 * Stores the raw ECharts event `params.data` array on mouse-over. An empty
 * array `[]` represents "no active hover", consistent with the prior Redux
 * initial state and the `DEFAULT_CHART_DEPENDENCIES.hoverDataPoint = []` convention.
 *
 * @metadata
 *  Layer        : Jotai
 *  Owner module : src/atoms/interaction.ts
 *  Scope        : global (one data-point hover at a time across all charts)
 *  Persist      : no
 *  Cross-tab    : no
 *  Reset on project switch : yes (implicit)
 *  Reset on auth change    : yes (implicit)
 *
 * Migrated from: groupBy.hoverDataPoint (Redux groupBySlice — @deprecated)
 */
export const hoverDataPointAtom = atom<(string | number)[]>([]);

// ---------------------------------------------------------------------------
// Widget Hover Map
// ---------------------------------------------------------------------------

/**
 * Per-widget hover flag used exclusively for toolbar / axis visibility gating.
 * A `true` entry for a widget ID means the pointer is currently within that widget.
 *
 * @metadata
 *  Layer        : Jotai
 *  Owner module : src/atoms/interaction.ts
 *  Scope        : global (shared across all chart widgets)
 *  Persist      : no
 *  Cross-tab    : no
 *  Reset on project switch : yes (implicit)
 *  Reset on auth change    : yes (implicit)
 *
 * Migrated from: app.widgetHoverMap (Redux appSlice — @deprecated)
 */
// Module-private — external code must read via `widgetHoverAtom` and write via
// `setWidgetHoverAtom`. Keeping this unexported prevents consumers from
// obtaining a direct write handle via `useAtom(widgetHoverBaseAtom)`, which
// would bypass the read-modify-write invariant in `setWidgetHoverAtom`.
const widgetHoverBaseAtom = atom<Record<string, boolean>>({});

/**
 * Read-only view of the widget hover map.
 * Consumers should subscribe to this atom, not the base, so that the
 * subscription point is explicit and the base write atom is not accidentally
 * read-written by consumers.
 */
export const widgetHoverAtom = atom<Record<string, boolean>>((get) =>
  get(widgetHoverBaseAtom)
);

/**
 * Write-only atom for updating a single widget's hover state.
 * Uses a read-modify-write pattern to preserve the hover state of other widgets.
 *
 * Usage:
 *   const setWidgetHover = useSetAtom(setWidgetHoverAtom);
 *   setWidgetHover({ widgetId: id, isHover: true });
 */
export const setWidgetHoverAtom = atom(
  null,
  (get, set, { widgetId, isHover }: { widgetId: string; isHover: boolean }) => {
    const current = get(widgetHoverBaseAtom);
    set(widgetHoverBaseAtom, { ...current, [widgetId]: isHover });
  }
);

// ---------------------------------------------------------------------------
// Hovered Well (Map)
// ---------------------------------------------------------------------------

/**
 * The UWI / entity ID of the well currently hovered on the Mapbox layer.
 * null = no well is currently hovered.
 *
 * @metadata
 *  Layer        : Jotai
 *  Owner module : src/atoms/interaction.ts
 *  Scope        : global (one hovered well at a time)
 *  Persist      : no
 *  Cross-tab    : no
 *  Reset on project switch : yes (implicit)
 *  Reset on auth change    : yes (implicit)
 *
 * Migrated from: map.hoveredWell (Redux mapSlice — @deprecated)
 * Note: no active read sites existed in production code at time of migration;
 *       atom is provided for future use and to enforce the ownership contract.
 */
export const hoveredWellAtom = atom<Maybe<string>>(null);
